/**
 * ai/aiOrchestrator.ts
 *
 * Central coordinator for all AI API calls.
 * Handles: throttling, cooldowns, retries, cost control,
 * JSON validation, fallbacks, and status reporting.
 *
 * The rest of the app never calls the AI API directly.
 * Everything goes through the orchestrator.
 */

import { MarketContext } from '../context/contextBuilder';
import { buildAIPrompt } from '../prompts/promptBuilder';
import { validateAIResponse, ValidatedAIResponse, fallbackResponse } from '../types/responseSchema';
import { applyAIResponse, getThesis } from '../thesis/thesisEngine';
import { logEvent } from '../../../features/status/systemEventLog';
import { now } from '../../../utils/clock';
import { logAIRun } from '../../../diagnostics/diagnosticsStore';
import { useStore } from '../../../store';

// Typed Anthropic response content block (avoids `any`)
interface AnthropicContentBlock { type: string; text?: string; }

// ─── AI mode ──────────────────────────────────────────────────────────────────

export type AIMode = 'ai_assisted' | 'deterministic_only' | 'ai_unavailable';

// ─── Orchestrator status ──────────────────────────────────────────────────────

export interface OrchestratorStatus {
  mode:             AIMode;
  lastSuccessAt:    number | null;
  lastAttemptAt:    number | null;
  lastFailureAt:    number | null;
  lastFailureReason: string | null;
  callCount:        number;
  failCount:        number;
  totalTokens:      number;
  cooldownEndsAt:   number | null;
  inFlight:         Set<string>;   // symbols currently being analyzed
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export interface OrchestratorConfig {
  minCooldownMs:    number;
  globalCooldownMs: number;
  maxRetries:       number;
  retryDelayMs:     number;
  model:            string;
  maxTokens:        number;
}

const DEFAULT_CONFIG: OrchestratorConfig = {
  minCooldownMs:    30_000,
  globalCooldownMs: 5_000,
  maxRetries:       2,
  retryDelayMs:     3_000,
  model:            'claude-sonnet-4-6',
  maxTokens:        4096,
};

export type AnalysisResult =
  | { success: true;  response: ValidatedAIResponse; fromCache: boolean }
  | { success: false; error: string; fallback: ValidatedAIResponse };

/** Type guard: narrows AnalysisResult to the success branch. */
export function isAnalysisSuccess(r: AnalysisResult): r is Extract<AnalysisResult, { success: true }> {
  return r.success === true;
}

export interface OrchestratorInstance {
  analyzeSymbol:              (ctx: MarketContext, force?: boolean) => Promise<AnalysisResult>;
  batchAnalyze:               (contexts: MarketContext[], onProgress?: (sym: string, done: number, total: number) => void) => Promise<Map<string, AnalysisResult>>;
  configureOrchestrator:      (cfg: Partial<OrchestratorConfig>) => void;
  getOrchestratorStatus:      () => OrchestratorStatus;
  subscribeOrchestratorStatus:(fn: () => void) => () => void;
  resetOrchestrator:          () => void;
}

export function createOrchestrator(
  initialConfig?: Partial<OrchestratorConfig>
): OrchestratorInstance {
  const status: OrchestratorStatus = {
    mode:              'deterministic_only',
    lastSuccessAt:     null,
    lastAttemptAt:     null,
    lastFailureAt:     null,
    lastFailureReason: null,
    callCount:         0,
    failCount:         0,
    totalTokens:       0,
    cooldownEndsAt:    null,
    inFlight:          new Set(),
  };
  let config = { ...DEFAULT_CONFIG, ...(initialConfig ?? {}) };
  const symbolLastCall  = new Map<string, number>();
  const statusListeners = new Set<() => void>();

  function notifyStatus() { statusListeners.forEach(fn => fn()); }

  function isSymbolOnCooldown(symbol: string): boolean {
    const last = symbolLastCall.get(symbol);
    return !!last && (now() - last) < config.minCooldownMs;
  }
  function isGlobalOnCooldown(): boolean {
    return !!status.lastAttemptAt && (now() - status.lastAttemptAt) < config.globalCooldownMs;
  }
  function cooldownRemaining(symbol: string): number {
    const last = symbolLastCall.get(symbol) ?? 0;
    return Math.max(0, Math.round((config.minCooldownMs - (now() - last)) / 1000));
  }

// ─── API call (single attempt) ────────────────────────────────────────────────

async function callAPI(prompt: string): Promise<string> {
  const body = JSON.stringify({
    model:      config.model,
    max_tokens: config.maxTokens,
    messages:   [{ role: 'user', content: prompt }],
  });

  // Try same-origin proxy first (server.js)
  const sameOrigin = typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.host}`
    : null;

  if (sameOrigin) {
    const proxyUp = await fetch(`${sameOrigin}/api/ai/health`, { credentials: 'include' })
      .then(r => r.ok).catch(() => false);

    if (proxyUp) {
      const resp = await fetch(`${sameOrigin}/api/ai`, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: { message: `HTTP ${resp.status}` } }));
        throw new Error(err?.error?.message || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const text = data?.content?.find((b: AnthropicContentBlock) => b.type === 'text')?.text ?? '';
      if (data?.usage?.output_tokens) status.totalTokens += data.usage.output_tokens;
      return text;
    }
  }

  // Fallback: claude.ai platform proxy (no key needed)
  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  });
  if (resp.status === 401) throw new Error('Not in claude.ai environment and proxy not running');
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = await resp.json();
  const text = data?.content?.find((b: AnthropicContentBlock) => b.type === 'text')?.text ?? '';
  if (data?.usage?.output_tokens) status.totalTokens += data.usage.output_tokens;
  return text;
}

// ─── Main analysis call ───────────────────────────────────────────────────────

type AnalysisResult =
  | { success: true;  response: ValidatedAIResponse; fromCache: boolean }
  | { success: false; error: string; fallback: ValidatedAIResponse };

async function analyzeSymbolFn(
  ctx:      MarketContext,
  force:    boolean = false,
): Promise<AnalysisResult> {
  const { symbol } = ctx;

  // Check if already in flight
  if (status.inFlight.has(symbol)) {
    return {
      success: false,
      error: 'Analysis already in progress for this symbol',
      fallback: fallbackResponse(symbol, 'In flight'),
    };
  }

  // Check cooldowns (unless forced)
  if (!force) {
    if (isSymbolOnCooldown(symbol)) {
      return {
        success: false,
        error: `Cooldown: ${cooldownRemaining(symbol)}s remaining`,
        fallback: fallbackResponse(symbol, 'Cooldown'),
      };
    }
    if (isGlobalOnCooldown()) {
      return {
        success: false,
        error: 'Global cooldown active',
        fallback: fallbackResponse(symbol, 'Global cooldown'),
      };
    }
  }

  // Unified skip guard: hash unchanged + thesis not stale + cooldown active → skip
  if (!force) {
    const thesis = getThesis(symbol);
    const hashUnchanged = thesis.lastInputHash === ctx.inputHash && thesis.current !== null;
    const notStale      = !thesis.isStale;
    const onCooldown    = cooldownRemaining(symbol) > 0 || isGlobalOnCooldown();

    if (hashUnchanged && notStale && onCooldown) {
      logAIRun(symbol, false, 'skipped: hash unchanged + not stale + cooldown active');
      return { success: true, response: thesis.current!, fromCache: true };
    }

    // Also skip on hash unchanged when thesis is fresh (regardless of cooldown)
    if (hashUnchanged && notStale) {
      logAIRun(symbol, false, 'skipped: hash unchanged, thesis current');
      return { success: true, response: thesis.current!, fromCache: true };
    }
  }

  // Get prior thesis for context
  const thesis      = getThesis(symbol);
  const priorThesis = thesis.current?.primaryThesis ?? null;
  const priorConv   = thesis.current?.conviction ?? null;
  // Phase 2 prompt fix (thesisChange semantics): pass structured prior
  // context so the AI can classify the change against an actionability
  // trajectory, not just narrative compare. Pulls from the same
  // thesis.current payload — no new state needed.
  const priorAction   = thesis.current?.actionability  ?? null;
  const priorSetup    = thesis.current?.bestSetupType  ?? null;
  const priorTradeNow = thesis.current?.shouldTradeNow ?? null;

  // Build prompt
  // Phase 3 commit 3.1: read trader horizon from the global Zustand store
  // outside the React render cycle. Hydrated by useUserPrefsHydration on
  // auth (commit 2.7). Defaults to 'intraday' if hydration hasn't run yet
  // or fetchHorizon failed silently.
  const traderHorizon = useStore.getState().horizon;
  const prompt = buildAIPrompt(ctx, priorThesis, priorConv, traderHorizon, priorAction, priorSetup, priorTradeNow);

  // Mark in-flight
  status.inFlight.add(symbol);
  status.lastAttemptAt = now();
  status.callCount++;
  symbolLastCall.set(symbol, now());
  notifyStatus();

  // Attempt with retries
  let lastError = '';
  const retryDelayMs = config.retryDelayMs;
  for (let attempt = 0; attempt < config.maxRetries + 1; attempt++) {
    try {
      if (attempt > 0) {
        await new Promise(r => setTimeout(r, retryDelayMs * attempt));
      }

      const rawText = await callAPI(prompt);
      const result  = validateAIResponse(rawText, symbol);

      if (!result._valid) {
        const inv = result as import('../types/responseSchema').InvalidAIResponse;
        lastError = inv._error;
        console.warn(`[AI] Validation failed (attempt ${attempt+1}):`, inv._error);
        continue;
      }

      // Success
      let validated = result as ValidatedAIResponse;

      // ── A1: Direction/conviction separation ─────────────────────────────────
      // If AI returned 'mixed' but regime + SMC + trend all agree directionally,
      // correct to directional bias with reduced conviction (not MIXED).
      // Reserve 'mixed' for genuine directional conflict only.
      if (validated.overallBias === 'mixed') {
        const regimeDom    = ctx.regime?.dominant;
        const smcBias      = ctx.smc?.bias;
        const trendDir     = ctx.trend?.trendDirection ?? 'neutral';
        const trendInTrend = ctx.trend?.trendState === 'trend' || ctx.trend?.trendState === 'pullback';

        const regimeBullish = regimeDom === 'breakout' || regimeDom === 'trend';
        const regimeBearish = regimeDom === 'meanReversion' && (ctx.regime?.vwapDist ?? 0) < -0.2;
        const smcBullish    = smcBias === 'bullish';
        const smcBearish    = smcBias === 'bearish';
        const trendBullish  = trendDir === 'bullish' && trendInTrend;
        const trendBearish  = trendDir === 'bearish' && trendInTrend;

        // Count directional signals
        const bullCount = (regimeBullish ? 1 : 0) + (smcBullish ? 1 : 0) + (trendBullish ? 1 : 0);
        const bearCount = (regimeBearish ? 1 : 0) + (smcBearish ? 1 : 0) + (trendBearish ? 1 : 0);

        if (bullCount >= 2 && bearCount === 0) {
          // All available directional signals are bullish — preserve direction, reduce conviction
          validated = {
            ...validated,
            overallBias: 'bullish',
            conviction:  Math.max(15, Math.round(validated.conviction * 0.75)),
            actionability: Math.max(10, Math.round(validated.actionability * 0.80)),
            keyDrivers: ['Direction preserved: regime+SMC+trend aligned bullish — low conviction from participation', ...validated.keyDrivers.slice(0, 2)],
          };
        } else if (bearCount >= 2 && bullCount === 0) {
          validated = {
            ...validated,
            overallBias: 'bearish',
            conviction:  Math.max(15, Math.round(validated.conviction * 0.75)),
            actionability: Math.max(10, Math.round(validated.actionability * 0.80)),
            keyDrivers: ['Direction preserved: regime+SMC+trend aligned bearish — low conviction from participation', ...validated.keyDrivers.slice(0, 2)],
          };
        }
        // If signals genuinely conflict → keep 'mixed' (correct behavior)
      }

      // ── A2: Deterministic gating — AI cannot override engine truth ──────────
      // Must be applied BEFORE applyAIResponse so no downstream consumer
      // ever sees shouldTradeNow:true when the deterministic gate is false.
      if (ctx.setupActionable === false && validated.shouldTradeNow) {
        validated = {
          ...validated,
          shouldTradeNow: false,
          actionability: Math.min(validated.actionability ?? 0, 40),
          urgency: validated.urgency === 'high' ? 'medium' : validated.urgency,
        };
      }

      applyAIResponse(validated);

      logAIRun(symbol, true, `Analysis completed — ${validated.thesisChange}`, validated.tokenCount);

      status.mode          = 'ai_assisted';
      status.lastSuccessAt = now();
      status.inFlight.delete(symbol);
      notifyStatus();
      logEvent('ai',
        `AI thesis updated — ${validated.thesisChange} (${validated.overallBias}, ${validated.conviction}%)`,
        symbol, validated.inputHash);

      return { success: true, response: validated, fromCache: false };

    } catch (e: unknown) {
      lastError = (e instanceof Error ? e.message : String(e)) || 'Unknown error';
      // Rate limit — wait longer
      if (lastError.includes('429') || lastError.includes('rate')) {
        await new Promise(r => setTimeout(r, 8000));
      }
    }
  }

  // All attempts failed
  status.failCount++;
  status.lastFailureAt     = now();
  status.lastFailureReason = lastError;
  status.mode              = 'ai_unavailable';
  status.inFlight.delete(symbol);
  notifyStatus();
  logEvent('ai', `AI refresh failed — ${lastError}`, symbol);

  const fb = fallbackResponse(symbol, lastError);
  return { success: false, error: lastError, fallback: fb };
}

// ─── Batch scan (multi-symbol) ────────────────────────────────────────────────
// Used by the scanner mode — sequential to avoid rate limits

async function batchAnalyzeFn(
  contexts:  MarketContext[],
  onProgress?: (symbol: string, done: number, total: number) => void
): Promise<Map<string, AnalysisResult>> {
  const results = new Map<string, AnalysisResult>();

  const globalCooldownMs = config.globalCooldownMs;
  for (let i = 0; i < contexts.length; i++) {
    const ctx = contexts[i];
    onProgress?.(ctx.symbol, i, contexts.length);
    const result = await analyzeSymbol(ctx, false);
    results.set(ctx.symbol, result);
    // Pause between calls to respect rate limits
    if (i < contexts.length - 1) {
      await new Promise(r => setTimeout(r, globalCooldownMs));
    }
  }

  onProgress?.('done', contexts.length, contexts.length);
  return results;
}

// ─── Reset state ──────────────────────────────────────────────────────────────

function resetOrchestratorFn() {
  status.mode              = 'deterministic_only';
  status.lastSuccessAt     = null;
  status.lastAttemptAt     = null;
  status.lastFailureAt     = null;
  status.lastFailureReason = null;
  status.callCount         = 0;
  status.failCount         = 0;
  status.totalTokens       = 0;
  status.cooldownEndsAt    = null;
  status.inFlight.clear();
  symbolLastCall.clear();
  notifyStatus();
}

  return {
    analyzeSymbol:               analyzeSymbolFn,
    batchAnalyze:                batchAnalyzeFn,
    configureOrchestrator:       (cfg: Partial<OrchestratorConfig>) => { config = { ...config, ...cfg }; },
    getOrchestratorStatus:       () => ({ ...status, inFlight: new Set(status.inFlight) }),
    subscribeOrchestratorStatus: (fn: () => void) => { statusListeners.add(fn); return () => statusListeners.delete(fn); },
    resetOrchestrator:           resetOrchestratorFn,
  };
}

// ─── Default singleton + re-exported named functions ──────────────────────────
// Preserves the exact same import surface as before this refactor.

const _default = createOrchestrator();

export const analyzeSymbol               = _default.analyzeSymbol;
export const batchAnalyze                = _default.batchAnalyze;
export function configureOrchestrator(cfg: Partial<OrchestratorConfig>) { _default.configureOrchestrator(cfg); }
export function getOrchestratorStatus()  { return _default.getOrchestratorStatus(); }
export function subscribeOrchestratorStatus(fn: () => void) { return _default.subscribeOrchestratorStatus(fn); }
export function resetOrchestrator()      { _default.resetOrchestrator(); }
